/* =========================================================================================================
   NJ THEME CUSTOM JS
   Moved unchanged out of assets/theme.js (it was appended at the end of Prestige's theme.js) so theme updates
   no longer overwrite it. Loaded as a module right after theme.js (layout/theme.liquid), same timing as before.
   Contains: product card gallery arrows + swipe, load-more pagination (<load-more-pagination>), and hiding the
   collection toolbar when the footer is visible.
   On touch devices the card swipe is handled by assets/nj-card-swipe.js (a strip that follows the finger); the
   swipe code below steps aside for cards that have that strip (data-nj-strip).
   ========================================================================================================= */
import { createMediaImg } from "theme"; // helper from Prestige theme.js (was in the same file before)


// -------------------------------------------------------------------------------------------------------------------
// Product card gallery arrows (collection grid)
// Keeps the existing secondary-image hover behaviour, but lets the user click next/prev to cycle the card images.
// -------------------------------------------------------------------------------------------------------------------

function getCardMediaList(productCardEl) {
  const raw = productCardEl?.getAttribute("data-card-media");
  if (!raw) return null;

  try {
    const list = JSON.parse(raw);
    const images = Array.isArray(list) ? list.filter((m) => m && m.media_type === "image" && m.preview_image) : [];
    return images.length > 0 ? images : null;
  } catch (e) {
    return null;
  }
}

function clampIndex(i, len) {
  if (len <= 0) return 0;
  return (i % len + len) % len;
}

// Lightweight in-memory preload cache so next/prev swaps feel instant.
// Keyed by the resolved `img.src` we generate for the card.
const __productCardImgCache = new Map(); // src -> Promise<void>

function __cacheImageLoad(img) {
  const src = img?.src;
  if (!src) return Promise.resolve();
  const existing = __productCardImgCache.get(src);
  if (existing) return existing;

  const p = new Promise((resolve) => {
    if (img.complete) return resolve();
    img.addEventListener("load", () => resolve(), { once: true });
    img.addEventListener("error", () => resolve(), { once: true });
  });
  __productCardImgCache.set(src, p);
  return p;
}

function warmProductCardCache(productCardEl, mediaList, aroundIndex, className, sizes) {
  if (!mediaList || mediaList.length < 2) return;
  // Preload a small window around the current/next image for snappy nav.
  const indices = [aroundIndex, aroundIndex + 1, aroundIndex - 1, aroundIndex + 2]
    .map((i) => clampIndex(i, mediaList.length))
    .filter((v, i, a) => a.indexOf(v) === i);

  for (const idx of indices) {
    const m = mediaList[idx];
    const img = createMediaImg(m, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: className, sizes });
    __cacheImageLoad(img);
  }
}

// NJ: desktop arrows slide the image in from the side (320ms) instead of swapping it instantly.
// The link clips the images; the new image is laid over the old one and both are moved with transforms.
const __njSlideStyle = document.createElement("style");
__njSlideStyle.textContent = ".product-card__media { overflow: hidden; } .product-card__image--nj-sliding { position: absolute; inset: 0; width: 100%; height: 100%; }";
document.head.appendChild(__njSlideStyle);

async function slideReplace(oldEl, newEl, direction) {
  const parent = oldEl.parentElement;
  if (!parent || !oldEl.animate) { oldEl.replaceWith(newEl); return; }
  const from = direction > 0 ? "100%" : "-100%";
  const to = direction > 0 ? "-100%" : "100%";
  newEl.classList.add("product-card__image--nj-sliding");
  newEl.style.opacity = "1";
  newEl.style.transform = `translateX(${from})`;
  parent.appendChild(newEl);
  await __cacheImageLoad(newEl);
  const timing = { duration: 320, easing: "cubic-bezier(.4, 0, .2, 1)", fill: "forwards" };
  const animations = [
    oldEl.animate([{ transform: "translateX(0)" }, { transform: `translateX(${to})` }], timing),
    newEl.animate([{ transform: `translateX(${from})` }, { transform: "translateX(0)" }], timing)
  ];
  // Safety net: never leave the card stuck if the animation does not finish (hidden tab, throttled browser)
  await Promise.race([
    Promise.all(animations.map((a) => a.finished)).catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, 700))
  ]);
  animations.forEach((a) => { try { a.cancel(); } catch (e) {} });
  newEl.classList.remove("product-card__image--nj-sliding");
  newEl.style.transform = "";
  newEl.style.opacity = "";
  oldEl.replaceWith(newEl);
}

async function setProductCardMedia(productCardEl, mediaList, newIndex, direction) {
  const primary = productCardEl.querySelector(".product-card__image--primary");
  if (!primary) return;

  const sizes = primary.getAttribute("sizes") || undefined;
  const classNamePrimary = primary.className;
  const nextIndex = clampIndex(newIndex, mediaList.length);
  const nextMedia = mediaList[nextIndex];

  // Build new primary image element
  const newPrimary = createMediaImg(nextMedia, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: classNamePrimary, sizes });

  // Warm cache so subsequent taps/swipes feel instant.
  warmProductCardCache(productCardEl, mediaList, nextIndex, classNamePrimary, sizes);

  // If the card already has a secondary image, keep it in sync as the "next" frame for hover
  const secondary = productCardEl.querySelector(".product-card__image--secondary");
  let newSecondary = null;
  if (secondary && mediaList.length > 1) {
    const secondaryIndex = clampIndex(nextIndex + 1, mediaList.length);
    const classNameSecondary = secondary.className;
    const nextSecondaryMedia = mediaList[secondaryIndex];
    newSecondary = createMediaImg(nextSecondaryMedia, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: classNameSecondary, sizes });
    newSecondary.setAttribute("aria-hidden", "true");
  }

  // Only swap if different
  if (primary.src === newPrimary.src) {
    if (secondary && newSecondary && secondary.src !== newSecondary.src) {
      secondary.replaceWith(newSecondary);
    }
    productCardEl.dataset.cardMediaIndex = String(nextIndex);
    return;
  }

  if (direction && secondary && newSecondary) {
    // Desktop arrows while hovering: the secondary (hover) image is the one on screen, so that is the one that slides.
    // The primary (hidden under it) is swapped silently.
    await __cacheImageLoad(newPrimary);
    primary.replaceWith(newPrimary);
    await slideReplace(secondary, newSecondary, direction);
    productCardEl.dataset.cardMediaIndex = String(nextIndex);
    return;
  }

  if (secondary && newSecondary) {
    secondary.replaceWith(newSecondary);
  }

  // Preload new primary before swapping to avoid flicker/blank frames.
  // Uses cache so if we already preloaded, this resolves immediately.
  await __cacheImageLoad(newPrimary);

  if (direction) {
    await slideReplace(primary, newPrimary, direction);
    productCardEl.dataset.cardMediaIndex = String(nextIndex);
    return;
  }

  // Swap instantly *after* preload. This avoids both flicker (blank frame) and
  // ghosting/double-exposure artifacts from crossfades on fast hover grids.
  primary.replaceWith(newPrimary);

  productCardEl.dataset.cardMediaIndex = String(nextIndex);
}

// Prevent nav buttons from stealing focus (which can keep nav visible after click)
document.addEventListener(
  "pointerdown",
  (event) => {
    const button = event.target.closest("[data-pc-nav]");
    if (!button) return;
    event.preventDefault();
  },
  { capture: true }
);

document.addEventListener(
  "click",
  async (event) => {
    const button = event.target.closest("[data-pc-nav]");
    if (!button) return;

    const productCardEl = button.closest("product-card");
    if (!productCardEl) return;

    const mediaList = getCardMediaList(productCardEl);
    if (!mediaList || mediaList.length < 2) return;

    event.preventDefault();
    event.stopPropagation();

    // Read current index (default to 0)
    const currentIndex = parseInt(productCardEl.dataset.cardMediaIndex || "0", 10) || 0;
    const direction = button.getAttribute("data-pc-nav");
    const delta = direction === "prev" ? -1 : 1;
    const newIndex = currentIndex + delta;

    // Avoid double taps while animating
    if (productCardEl.dataset.cardMediaBusy === "1") return;
    productCardEl.dataset.cardMediaBusy = "1";

    try {
      await setProductCardMedia(productCardEl, mediaList, newIndex, delta);
    } finally {
      productCardEl.dataset.cardMediaBusy = "0";
    }
  },
  { capture: true }
);

// Opportunistic preloading: when the user first interacts with a product card image on desktop,
// preload a small window of images so arrow navigation feels immediate.
document.addEventListener(
  "pointerenter",
  (event) => {
    if (!window.matchMedia || !window.matchMedia("(pointer: fine)").matches) return;
    const mediaEl = event.target.closest?.(".product-card__media");
    if (!mediaEl) return;
    const productCardEl = mediaEl.closest("product-card");
    if (!productCardEl) return;
    const mediaList = getCardMediaList(productCardEl);
    if (!mediaList || mediaList.length < 2) return;
    const primary = productCardEl.querySelector(".product-card__image--primary");
    if (!primary) return;
    const sizes = primary.getAttribute("sizes") || undefined;
    const classNamePrimary = primary.className;
    const currentIndex = parseInt(productCardEl.dataset.cardMediaIndex || "0", 10) || 0;
    warmProductCardCache(productCardEl, mediaList, currentIndex, classNamePrimary, sizes);
  },
  { capture: true }
);

// Swipe navigation on touch devices (mobile/tablet)
// - Arrows are hidden on coarse pointers, so this provides the intended UX.
// - We only intercept when the gesture is a clear horizontal swipe.
// - NJ: cards with the swipe strip (assets/nj-card-swipe.js, data-nj-strip) are left to the browser's own scrolling.
(function enableProductCardSwipe() {
  const isCoarsePointer = () => window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

  let active = null; // { card, mediaList, startX, startY, pointerId, swiping }

  const reset = () => {
    active = null;
  };

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (!isCoarsePointer()) return;
      if (event.pointerType !== "touch" && event.pointerType !== "pen") return;
      if (event.button != null && event.button !== 0) return;

      // Don't start swipe on buttons/controls
      if (event.target.closest("button, [role='button'], .product-card__quick-add-button")) return;

      const mediaLink = event.target.closest(".product-card__media");
      if (!mediaLink) return;

      const productCardEl = mediaLink.closest("product-card");
      if (!productCardEl) return;
      if (productCardEl.dataset.njStrip) return; // handled by nj-card-swipe.js

      const mediaList = getCardMediaList(productCardEl);
      if (!mediaList || mediaList.length < 2) return;

      active = {
        card: productCardEl,
        mediaList,
        startX: event.clientX,
        startY: event.clientY,
        pointerId: event.pointerId,
        swiping: false
      };

      // Preload adjacent images on first touch so swiping feels instant.
      const primary = productCardEl.querySelector(".product-card__image--primary");
      if (primary) {
        const sizes = primary.getAttribute("sizes") || undefined;
        const classNamePrimary = primary.className;
        const currentIndex = parseInt(productCardEl.dataset.cardMediaIndex || "0", 10) || 0;
        warmProductCardCache(productCardEl, mediaList, currentIndex, classNamePrimary, sizes);
      }
    },
    { capture: true }
  );

  document.addEventListener(
    "pointermove",
    (event) => {
      if (!active) return;
      if (event.pointerId !== active.pointerId) return;
      if (active.card.dataset.njStrip) { reset(); return; }

      const dx = event.clientX - active.startX;
      const dy = event.clientY - active.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      // Decide if it's a horizontal swipe (avoid blocking vertical scroll)
      if (!active.swiping) {
        if (adx < 12) return;
        if (ady > adx) {
          // User is scrolling vertically
          reset();
          return;
        }
        active.swiping = true;
      }

      // Once swiping, prevent the browser from treating it as a scroll/drag on the link
      event.preventDefault();
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    "pointerup",
    async (event) => {
      if (!active) return;
      if (event.pointerId !== active.pointerId) return;
      if (active.card.dataset.njStrip) { reset(); return; }

      const dx = event.clientX - active.startX;
      const dy = event.clientY - active.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      const isSwipe = adx >= 35 && adx > ady * 1.2;
      if (!isSwipe) {
        reset();
        return;
      }

      // Prevent link navigation click after swipe
      const cardForCancel = active.card;
      cardForCancel.dataset.cardMediaSwipeCancel = "1";
      window.setTimeout(() => {
        cardForCancel.dataset.cardMediaSwipeCancel = "0";
      }, 250);

      event.preventDefault();
      event.stopPropagation();

      const currentIndex = parseInt(active.card.dataset.cardMediaIndex || "0", 10) || 0;
      const delta = dx > 0 ? -1 : 1; // swipe right = previous, swipe left = next
      const newIndex = currentIndex + delta;

      if (active.card.dataset.cardMediaBusy === "1") {
        reset();
        return;
      }
      active.card.dataset.cardMediaBusy = "1";

      try {
        await setProductCardMedia(active.card, active.mediaList, newIndex);
      } finally {
        active.card.dataset.cardMediaBusy = "0";
        reset();
      }
    },
    { capture: true }
  );

  // Cancel navigation click if it was preceded by a swipe
  document.addEventListener(
    "click",
    (event) => {
      const mediaLink = event.target.closest(".product-card__media");
      if (!mediaLink) return;
      const productCardEl = mediaLink.closest("product-card");
      if (!productCardEl) return;
      if (productCardEl.dataset.cardMediaSwipeCancel === "1") {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    { capture: true }
  );
})();


/* -------------------------------------------------------------------------------------------------
 * LOAD MORE PAGINATION (Collections)
 * ------------------------------------------------------------------------------------------------- */
if (!customElements.get('load-more-pagination')) {
  class LoadMorePagination extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-load-more-button]');
      this.loadedCountEl = this.querySelector('[data-loaded-count]');
      this.totalCountEl = this.querySelector('[data-total-count]');
      this.barEl = this.querySelector('[data-load-more-bar]');
      this.progressEl = this.querySelector('.load-more-pagination__bar[role="progressbar"]');
      if (!this.button) return;

      // Don’t let the button keep focus (can cause sticky UI states in some setups)
      this.button.addEventListener('pointerdown', (e) => e.preventDefault());

      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      // Initial UI sync
      this.updateProgress();
    }

    disconnectedCallback() {
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
    }

    async onClick(e) {
      e.preventDefault();

      // Prevent parallel clicks / double triggers
      if (this._busy) return;
      this._busy = true;

      // If this has been converted to a “next collection” button, navigate.
      const nextCollectionUrl = this.dataset.nextCollectionUrl;
      if (this.button.hasAttribute('data-next-collection-button') && nextCollectionUrl) {
        window.location.href = nextCollectionUrl;
        this._busy = false;
        return;
      }

      const nextUrl = this.button?.dataset?.nextUrl;
      if (!nextUrl) {
        // No next URL. If we have a next collection, convert the button.
        if (nextCollectionUrl) {
          this.activateNextCollection();
        }
        this._busy = false;
        return;
      }

      const renderSection = this.dataset.renderSection || '';
      const url = new URL(nextUrl, window.location.origin);

      // Ask Shopify to return only the section HTML so we can append cleanly
      if (renderSection) url.searchParams.set('section_id', renderSection);

      this.setLoading(true);

      try {
        const res = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(`Load more request failed: ${res.status}`);
        const html = await res.text();

        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Find the product list inside the returned section and append its children
        const incomingList = doc.querySelector('product-list');
        const currentList = this.closest('.shopify-section')?.querySelector('product-list');

        if (!incomingList || !currentList) {
          // Fallback: if we can’t find the list, degrade gracefully by navigating
          window.location.href = nextUrl;
          return;
        }

        // If the next page returned no products, we’re done
        const nodes = Array.from(incomingList.children);
        if (nodes.length === 0) {
          if (this.dataset.nextCollectionUrl) {
            this.activateNextCollection();
          } else {
            this.remove();
          }
          return;
        }
// Append product cards (dedupe by product handle to prevent overlap across pages)
        const existingHandles = new Set(
          Array.from(currentList.querySelectorAll('product-card[handle]')).map((el) => el.getAttribute('handle'))
        );

        nodes.forEach((n) => {
          // NJ: collection promo blocks (Prestige 11 "push_promo") are placed by position on every page. With
          // "load more" they would repeat inside the grid on each loaded page, so only the ones from page 1 are kept.
          if (n && n.classList && n.classList.contains('product-list__promo')) return;

          // If the card already exists (can happen in some paginate-by edge cases), skip it
          if (n && n.matches && n.matches('product-card[handle]')) {
            const h = n.getAttribute('handle');
            if (h && existingHandles.has(h)) return;
            if (h) existingHandles.add(h);
          }
          currentList.appendChild(n);
        });

        // Update “Showing X of Y” + progress bar
        this.updateProgress(currentList);

        // If we've reached total count, switch to NEXT collection (or remove if none)
        try {
          const total = parseInt(this.dataset.totalCount || '0', 10) || 0;
          if (total) {
            const loadedNow = currentList.querySelectorAll('product-card, .product-card, .product-item').length;
            if (loadedNow >= total) {
              if (this.dataset.nextCollectionUrl) {
                this.activateNextCollection();
              } else {
                this.remove();
              }
              return;
            }
          }
        } catch (e) {}

        // Update button to next page (if any)
        // NOTE: section-render responses can vary; don’t rely on wrapper selector only.
        const incomingBtn =
          doc.querySelector('[data-load-more-button][data-next-url]') ||
          doc.querySelector('load-more-pagination [data-load-more-button][data-next-url]');

        if (incomingBtn && incomingBtn.dataset.nextUrl) {
          this.button.dataset.nextUrl = incomingBtn.dataset.nextUrl;
          this.setLoading(false);

        } else {
          // If we can't find the updated next URL in the returned HTML, we must not guess.
          // Convert to NEXT collection if available, otherwise remove to avoid a dead button.
          if (this.dataset.nextCollectionUrl) {
            this.activateNextCollection();
          } else {
            this.remove();
          }
        }
      } catch (err) {
        console.error(err);
        // Graceful fallback: go to next page
        window.location.href = nextUrl;
      } finally {
        this._busy = false;
        // Only remove loading state if element still exists and still has a button
        if (this.isConnected && this.querySelector('[data-load-more-button]')) {
          this.setLoading(false);
        }
      }
    }

    setLoading(isLoading) {
      if (!this.button) return;
      this.button.toggleAttribute('aria-busy', isLoading);
      this.button.disabled = isLoading;
      this.button.classList.toggle('is-loading', isLoading);
    }

    updateProgress(currentList) {
      try {
        const list = currentList || this.closest('.shopify-section')?.querySelector('product-list');
        if (!list) return;

        const loaded = list.querySelectorAll('product-card, .product-card, .product-item').length;
        const total = parseInt(this.dataset.totalCount || this.totalCountEl?.textContent || '0', 10) || 0;

        if (this.loadedCountEl) this.loadedCountEl.textContent = String(loaded);
        if (this.totalCountEl && total) this.totalCountEl.textContent = String(total);

        if (this.progressEl) {
          if (total) {
            this.progressEl.setAttribute('aria-valuemax', String(total));
            this.progressEl.setAttribute('aria-valuenow', String(Math.min(loaded, total)));
          } else {
            this.progressEl.setAttribute('aria-valuenow', String(loaded));
          }
        }

        if (this.barEl) {
          const pct = total ? Math.max(0, Math.min(100, (loaded / total) * 100)) : 0;
          this.barEl.style.width = pct.toFixed(4) + '%';
        }
      } catch (e) {}
    }

    activateNextCollection() {
      const url = this.dataset.nextCollectionUrl;
      if (!url || !this.button) {
        this.remove();
        return;
      }

      const title = this.dataset.nextCollectionTitle || '';
      this.button.dataset.nextUrl = '';
      this.button.setAttribute('data-next-collection-button', '');

      // Update label (keep language parity)
      const isSv = (document.documentElement.lang || '').toLowerCase().includes('sv');
      const label = title ? (isSv ? `NÄSTA: ${title}` : `NEXT: ${title}`) : (isSv ? 'NÄSTA' : 'NEXT');
      this.button.textContent = label;

      // Keep progress in sync at end state
      this.updateProgress();
    }
  }

  customElements.define('load-more-pagination', LoadMorePagination);
}
document.addEventListener("DOMContentLoaded", () => {
  const toolbar =
    document.querySelector(".collection-toolbar") ||
    document.querySelector(".collection__toolbar") ||
    document.querySelector("[data-collection-toolbar]");

  const footer =
    document.querySelector("footer") ||
    document.querySelector(".shopify-section--footer") ||
    document.querySelector("#shopify-section-footer");

  if (!toolbar || !footer) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        toolbar.classList.add("is-hidden-by-footer");
      } else {
        toolbar.classList.remove("is-hidden-by-footer");
      }
    },
    { threshold: 0 }
  );

  observer.observe(footer);
});
